import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
} from 'react-native';

export const LIST_ITEM_HEIGHT = 76;
export const LIST_ITEM_MARGIN = 16;

interface Props {
  bottomLeftComponent?: React.ReactNode;
  bottomRightText?: string;
  footer?: React.ReactNode;
  onPress?: () => void;
  showTopRightIcon?: boolean;
  testID?: string;
  textStyles?: {
    bottomRight?: StyleProp<TextStyle>;
    topLeft?: StyleProp<TextStyle>;
    topRight?: StyleProp<TextStyle>;
  };
  topLeftText: string;
  topRightIcon?: React.ReactNode;
  topRightText: string;
}

export const HistoryListItem: React.FC<Props> = ({
  bottomLeftComponent = null,
  bottomRightText,
  onPress,
  showTopRightIcon = false,
  testID,
  textStyles = {},
  topLeftText,
  topRightIcon,
  topRightText,
  footer,
}) => {
  const [styles] = useStyles();
  return (
    <TouchableOpacity disabled={!onPress} onPress={onPress} testID={testID}>
      <View
        style={[
          styles.base,
          footer ? styles.baseWithFooter : styles.baseWithoutFooter,
        ]}
      >
        <View style={styles.row}>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.h3, textStyles.topLeft, styles.ellipsizedText]}
          >
            {topLeftText}
          </Text>
          <View
            style={[styles.icon, showTopRightIcon ? styles.iconVisible : null]}
          >
            {topRightIcon}
          </View>
          {topRightText ? (
            <Text
              style={[TYPOGRAPHY.h3, styles.priceText, textStyles.topRight]}
            >
              {topRightText}
            </Text>
          ) : null}
        </View>
        {bottomLeftComponent || bottomRightText ? (
          <View style={styles.row}>
            {bottomLeftComponent}
            {bottomRightText ? (
              <Text
                numberOfLines={1}
                style={[
                  styles.bottomRowText,
                  textStyles.bottomRight,
                  styles.ellipsizedText,
                ]}
              >
                {bottomRightText}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </TouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
    height: LIST_ITEM_HEIGHT,
    justifyContent: 'center',
    paddingVertical: 12,
  },
  baseWithFooter: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  baseWithoutFooter: {
    borderRadius: 4,
  },
  bottomRowText: {
    ...FONTS.primaryBold,
    color: theme.colors.inactiveText,
    flexGrow: 1,
    fontSize: theme.fontSizes.xsmall,
    textAlign: 'right',
    textTransform: 'uppercase',
  },
  ellipsizedText: {
    flex: 1,
  },
  footer: {
    backgroundColor: theme.colors.white,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  icon: {
    marginLeft: 'auto',
    // Set opacity so the row height doesn't change depending on whether
    // there's an icon or not
    opacity: 0,
  },
  iconVisible: {
    opacity: 1,
  },
  priceText: {
    marginLeft: 4,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 4,
  },
}));
